import { IonButton } from '@ionic/react';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { UseMutationResult } from 'react-query';
import { useNavigate } from 'react-router';
import { AnyObject } from 'yup';
import { ALERT_CREDITS_LIMIT, useRemainingCredits } from '../../../api/hooks/sales/useRemainingCredits';
import { useCreateUpdateSale } from '../../../api/hooks/sales/useUpdateCreateSale';
import { isProviderEnabledForDoc } from '../../../config/utils/providerUtils';
import {
  addDefaultZeroToPrices,
  cleanProducts,
  getChangedFields,
  removeIdsFromObjects,
} from '../../../config/utils/formDiffUtils';
import { formDataToSubmit, getInitialFormData } from '../../../config/utils/formUtils';
import { GenericResponse } from '../../../interfaces/shared.interface';
import { PATHS } from '../../../navigation/data';
import { isObjectEmpty } from '../../../services/utils/helpers';
import { isCreditConsumingDocType } from '../../../services/utils/myDataUtils';
import { hasCompanyProviderEnabled, selectCompanyProviderSettings } from '../../../store/companies/companiesSlice';
import { setToast } from '../../../store/core/coreSlice';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { Loading } from '../../atoms/Loading';
import { CustomerForm } from './CustomerForm';
import { DocFooter } from './DocFooter';
import { DocMainInfoSection } from './DocMainInfoSection';
import { DocPaymentDelivery } from './DocPaymentDelivery';
import { DocProducts } from './DocProducts';

interface Props {
  data: any;
  validationSchema: AnyObject;
  submitHook?: () => UseMutationResult<GenericResponse, Error, void, unknown>;
  deleteHook?: () => UseMutationResult<GenericResponse, Error, void, unknown>;
  isClone?: boolean;
  isCorrelate?: boolean;
}

export const DocForm = ({ data, validationSchema, isClone, isCorrelate }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const providerSettings = useAppSelector(selectCompanyProviderSettings);
  const hasProviderEnabled = useAppSelector(hasCompanyProviderEnabled);
  const { t } = useTranslation();
  const { mutate: createModel, isLoading: isLoadingMutate, isSuccess, data: resp } = useCreateUpdateSale();
  // Disabled: never auto-fetches on mount/focus - only triggered imperatively after a successful
  // save, below. Its own `onSuccess` (see `useRemainingCredits.tsx`) dispatches the low-credits
  // toast when the result comes back low.
  const { refetch: refetchRemainingCredits } = useRemainingCredits(false);
  // Set at submit time, read back once the save succeeds (the effect below has no other way to
  // know the submitted doc's `aadeCode`).
  const lastAadeCodeRef = useRef<any>(undefined);
  const initialFormData = useMemo(() => getInitialFormData(data), [data]);
  // Named field access — avoids brittle array-index coupling
  const f = useMemo(() => (name: string) => data.fields.find((field: any) => field.name === name), [data.fields]);

  useEffect(() => {
    if (!isSuccess) return;

    const run = async () => {
      if (resp?.success) {
        // Both this toast and the low-credits one share a single global toast slot
        // (`coreSlice.ts`) - checking credits first and awaiting it (while still mounted, before
        // navigating away) guarantees that if it fires its own toast, the plain "update
        // successful" below never fires after it and clobbers it. Skip it outright when credits
        // come back low, rather than racing a timer against navigation unmounting this component.
        let creditsAreLow = false;
        if (hasProviderEnabled && isCreditConsumingDocType(lastAadeCodeRef.current ?? null)) {
          const result = await refetchRemainingCredits();
          creditsAreLow = typeof result.data === 'number' && result.data < ALERT_CREDITS_LIMIT;
        }

        if (!creditsAreLow && !resp?.errorCode) {
          dispatch(
            setToast({
              value: { show: true, msg: resp?.error || t('SHARED.UPDATE_SUCCESSFUL'), isError: false },
            }),
          );
        }

        if (resp?.entity?.id) {
          navigate(`${PATHS.SALES_VIEW}/${resp?.entity?.id}`, { replace: true });
        } else {
          navigate(-1);
        }
        return;
      }

      // Interceptor already showed the toast for errorCode responses
      if (resp?.errorCode) return;
      dispatch(setToast({ value: { show: true, msg: resp?.error || t('SHARED.UPDATE_SUCCESSFUL'), isError: true } }));
    };

    run();
  }, [dispatch, hasProviderEnabled, isSuccess, navigate, refetchRemainingCredits, resp, t]);

  const formik = useFormik({
    initialValues: initialFormData,
    validationSchema,
    onSubmit: (values) => {
      // console.log('==================================');
      // console.log('initialFormData: ', initialFormData);
      // console.log('values: ', values);
      let dataToSubmit: Record<string, any> = JSON.parse(JSON.stringify(values));
      // `post_with_provider` is normally kept in sync reactively by `ProviderToggle` as the doc
      // type changes, but that's a `useEffect` — it lags a render behind the doc type change. A
      // submit that races ahead of that effect (e.g. changing doc type then saving immediately)
      // would otherwise send a stale value. Recompute it synchronously here so submission can
      // never race the effect; the BE also re-derives and corrects it as a second line of defense.
      const aadeCode = dataToSubmit?.documentTypeId?.object?.aadeCode || dataToSubmit?.documentTypeId?.aadeCode;
      dataToSubmit.post_with_provider = isProviderEnabledForDoc(providerSettings, aadeCode);
      lastAadeCodeRef.current = aadeCode;
      addDefaultZeroToPrices(dataToSubmit.products);
      // console.log('dataToSubmit 2: ', dataToSubmit);
      // return;

      if (data.id && !isClone) {
        const changedFields = getChangedFields(data.fields, initialFormData, dataToSubmit);

        // Nothing changed, triggered info message
        if (isObjectEmpty(changedFields)) {
          dispatch(
            setToast({
              value: {
                show: true,
                msg: t('SHARED.NO_CHANGES'),
                isError: false,
              },
            }),
          );
          return;
        }

        dataToSubmit = {
          id: data.id,
          ...changedFields,
        };

        if (dataToSubmit.products) {
          dataToSubmit.products = cleanProducts(initialFormData.products, dataToSubmit.products);
        }
      }

      // Remove ids from products if clone or correlate
      if (isClone || isCorrelate) {
        dataToSubmit.products = removeIdsFromObjects(dataToSubmit.products);
      }
      // return;

      const finalData = formDataToSubmit(data.fields, dataToSubmit);

      // console.log('DocForm finalData: ', finalData);
      // return;

      // @ts-expect-error mutation interface
      createModel({ ...finalData, isClone: isClone || isCorrelate });
    },
  });

  // useEffect(() => {
  //   console.log('====== formik.errors: ', formik.errors);
  //   console.log('====== formik.values: ', formik.values);
  // }, [formik.errors, formik.values]);

  const isProductsSectionDisabled = useMemo(() => !!formik?.errors?.customerId, [formik?.errors]);

  const getProductsSectionDisabledOverlay = useCallback(() => {
    if (isProductsSectionDisabled) {
      return <div className="disabled-section"></div>;
    }
  }, [isProductsSectionDisabled]);

  return (
    <div className="scrollable padding-b-10">
      {isLoadingMutate && <Loading />}
      <form onSubmit={formik.handleSubmit}>
        <div className="padding-16">
          <div className="margin-b-16">
            <CustomerForm
              field={f('customerId')}
              vatRegimeField={f('vatRegimeId')}
              vatExceptionCategoryField={f('vatExceptionCategoryId')}
              providerField={f('post_with_provider')}
              formik={formik}
            />
          </div>
          <div className="margin-b-16">
            <DocMainInfoSection
              type={f('documentTypeId')}
              purposeField={f('docPurposeId')}
              date={f('documentDate')}
              paymentField={f('paymentTypeId')}
              seriesName={f('seriesName')}
              serialNumber={f('serialNumber')}
              posField={f('pos')}
              purposeField2={f('docPurpose2Id')}
              receivingNotePurposeField={f('receivingNotePurpose')}
              otherReceivingNotePurposeTitleField={f('otherReceivingNotePurposeTitle')}
              isDeliveryNoteField={f('isDeliveryNote')}
              resetSerialNumberField={f('resetSerialNumber')}
              nonObligatedRecipientField={f('nonObligatedRecipient')}
              withoutDigitalTransportTrackingField={f('withoutDigitalTransportTracking')}
              formik={formik}
              isClone={isClone}
              isCorrelate={isCorrelate}
            />
          </div>
          <div className="margin-b-16 positionRelative">
            {getProductsSectionDisabledOverlay()}
            <DocProducts
              field={f('products')}
              formik={formik}
              isSectionDisabled={isProductsSectionDisabled}
              taxWithholdField={f('taxWithholdId')}
              productsToBeWeighedField={f('productsToBeWeighed')}
            />
          </div>
          <div className="margin-b-16">
            <DocPaymentDelivery
              shippingField={f('shipping')}
              dueDate={f('dueDate')}
              shipDate={f('shipDate')}
              shipFrom={f('shipFrom')}
              shipTo={f('shipTo')}
              shipToCity={f('shipToCity')}
              shipToPostalCode={f('shipToPostalCode')}
              formik={formik}
            />
          </div>
          <div className="margin-b-16">
            <DocFooter field={f('notes')} formik={formik} />
          </div>
        </div>
        <div className="flex-center-center-row-wrap">
          <IonButton mode="md" type="submit" disabled={!formik.isValid || !formik.dirty} className="button-focused">
            {t('FORMS.SAVE')}
          </IonButton>
        </div>
      </form>
    </div>
  );
};
